import { RepositoryDataCollection, AfterSaveContext } from "@tylertech/tyler-api-types";
import { Utilities } from "@tylertech/shared-module";
import { CachedRepositoryService, RepositoryService } from "@tylertech/core-module";
import { ObjectHelpers } from "../utilities";
export class RepositoryFormData implements RepositoryDataCollection
{
    ReturnDataOnSave: boolean;

    Entity: any;

    Type: string;

    OtherEntities: Record<string, any[]> = {};

    ConfirmationQuestions: Record<string, boolean> = {};

    OptionalParentEntity: any;

    OptionalParentType: string;

    EntityComparator: (current: any, other: any) => boolean;

    AfterSaveContext: AfterSaveContext;

    $FetchPromises: Record<string, Promise<any>> = {};

    $OtherEntitiesByLoadFilter: Record<string, any[]> = {};

    $ParentFormData: RepositoryFormData;

    constructor(boundType: string, model: any)
    {
        this.Type = boundType;
        this.Entity = model;
    }

    InitializeFormData(): void
    {
        if (this.Entity)
        {
            this.setFormData(this.Entity);
        }
        for (const field in this.OtherEntities)
        {
            const a = this.OtherEntities[field];
            if (a)
            {
                for (let index = 0; index < a.length; index++)
                {
                    const element = a[index];
                    this.setFormData(element);
                }
            }
        }
    }

    setFormData(entity: any): void
    {
        entity["$FormData"] = this;
    }

    SetEntityComparator(comparator: (current: any, other: any) => boolean): void
    {
        this.EntityComparator = comparator;
    }

    GetWithoutFetch(viewClassType: string, filter: string): any
    {
        const key = `${viewClassType}|${filter}`;
        if (this.$OtherEntitiesByLoadFilter[key])
        {
            return this.$OtherEntitiesByLoadFilter[key];
        }
        return null;
    }

    GetOrFetch(
        svc: CachedRepositoryService,
        viewClassType: string,
        filter: string = "",
        ignoreCache: boolean = false
    ): Promise<any[]>
    {
        const self = this;
        const key = `${viewClassType}|${filter}`;
        if (this.$OtherEntitiesByLoadFilter[key])
        {
            return Promise.resolve(this.$OtherEntitiesByLoadFilter[key]);
        }
        const promise =
            this.$FetchPromises[key] ||
            svc.FetchCache(viewClassType, filter, ignoreCache)
                .then(entities =>
                {
                    delete self.$FetchPromises[key];
                    if (entities) self.$OtherEntitiesByLoadFilter[key] = entities.slice();
                    return self.$OtherEntitiesByLoadFilter[key];
                });
        this.$FetchPromises[key] = promise;
        return promise;
    }

    findExistingOrGet(rowTypeName: string, idProperty: string, id: string, $repositoryService: RepositoryService, questions?: Record<string, boolean>, filter?: string): Promise<any>
    {
        // With projection grid, user may need to edit the newly created but unsaved entity
        // where the entity should be in the OtherEntities already, if it isn't, we will
        // get it from server
        const existingObjects = this.OtherEntities[rowTypeName];
        if (!Utilities.IsNullOrUndefined(existingObjects))
        {
            const matchingObject = existingObjects.find(item =>
            {
                return item[idProperty] === id;
            });
            if (!Utilities.IsNullOrUndefined(matchingObject))
            {
                return Promise.resolve(matchingObject);
            }
        }
        return $repositoryService.GetWithQuestions({
            Type: rowTypeName,
            Id: id,
            AskedQuestions: questions,
        }, filter);
    }

    IsInCache(svc: CachedRepositoryService, viewClassType: string, action: string, filter: string = ""): boolean
    {
        const key = `${viewClassType}|${filter}`;
        return !Utilities.IsNullOrUndefined(this.$FetchPromises[key]) || svc.IsInCache(viewClassType, action, filter);
    }

    AddAll(key: string, values: any[], filter: string = ""): void
    {
        if (this.OtherEntities[key])
        {
            for (let index = 0; index < values.length; index++)
            {
                this.Add(key, values[index], filter);
                this.setFormData(values[index]);
            }
            this.$OtherEntitiesByLoadFilter[`${key}|${filter}`] = values;
        }
        else if (!filter)
            this.OtherEntities[key] = values;
        else
        {
            this.OtherEntities[key] = values.slice();
            this.$OtherEntitiesByLoadFilter[`${key}|${filter}`] = values;
        }
    }

    Clear(key: string): void
    {
        delete this.OtherEntities[key];
        const array = Object.keys(this.$OtherEntitiesByLoadFilter);
        for (let index = 0; index < array.length; index++)
        {
            const e = array[index];
            if (e.startsWith(key))
            {
                delete this.$OtherEntitiesByLoadFilter[e];
            }
        }
    }

    ClearAll(): void
    {
        this.$ParentFormData?.ClearAll();
        this.Entity = undefined;
        this.OtherEntities = {};
        this.$OtherEntitiesByLoadFilter = {};
    }

    Add(typeName: string, value: any, filter?: any): void
    {
        let cachedValue = this.OtherEntities[typeName];
        if (!cachedValue)
        {
            cachedValue = [];
            this.OtherEntities[typeName] = cachedValue;
        }
        if (!this.Contains(cachedValue, value))
        {
            cachedValue.push(value);
            this.setFormData(value);
            if (filter !== undefined && this.$OtherEntitiesByLoadFilter[`${typeName}|${filter}`])
            {
                if (!this.Contains(this.$OtherEntitiesByLoadFilter[`${typeName}|${filter}`], value))
                    this.$OtherEntitiesByLoadFilter[`${typeName}|${filter}`].push(value);
            }
            else
                this.$OtherEntitiesByLoadFilter[`${typeName}|${filter}`] = [value];
        }
    }

    AddToTop(typeName: string, value: any, filter: any): void
    {
        let cachedValue = this.OtherEntities[typeName];
        if (!cachedValue)
        {
            cachedValue = [];
            this.OtherEntities[typeName] = cachedValue;
        }
        if (!this.Contains(cachedValue, value))
        {
            cachedValue.unshift(value);
            this.setFormData(value);
            if (filter && this.$OtherEntitiesByLoadFilter[`${typeName}|${filter}`])
                this.$OtherEntitiesByLoadFilter[`${typeName}|${filter}`].unshift(value);
        }
    }

    Contains(cachedValue: any[], isIn: any): boolean
    {
        if (isIn.$isEntityWrapper) isIn = isIn.$value;
        return cachedValue.filter(x => (x.$isEntityWrapper ? x.$value === isIn : x === isIn)).length > 0;
    }

    Remove(typeName: string, value: any, filter: any): void
    {
        const k = this.OtherEntities[typeName];
        if (k)
        {
            const index = k.indexOf(value);
            if (index > -1)
            {
                k.splice(index, 1);
                if (this.$OtherEntitiesByLoadFilter[`${typeName}|${filter}`])
                {
                    const index = this.$OtherEntitiesByLoadFilter[`${typeName}|${filter}`].indexOf(value);
                    if (index > -1) this.$OtherEntitiesByLoadFilter[`${typeName}|${filter}`].splice(index, 1);
                }
            }
        }
    }

    RemoveEntity(typeName: string, originalEntity: any): void
    {
        const k = this.OtherEntities[typeName];
        if (k)
        {
            // remove from OtherEntities first, if nothing found, no further actions are needed.
            const index = k.findIndex(item => item["Id"] === originalEntity["Id"]);
            if (index < 0)
            {
                return;
            }
            k.splice(index, 1);
        }
        // remove from subproperty on parent
        const parent = this.Entity;
        if (parent)
        {
            for (const field in parent)
            {
                const subproperty = parent[field];
                if (Array.isArray(subproperty))
                {
                    const childIndex = subproperty.findIndex(
                        child => child["$$TypeName"] === typeName && originalEntity["Id"] && child["Id"] === originalEntity["Id"]
                    );
                    if (childIndex > -1)
                    {
                        subproperty.splice(childIndex, 1);
                    }
                }
                else if (
                    subproperty &&
                    subproperty["$$TypeName"] === typeName &&
                    originalEntity["Id"] &&
                    subproperty["Id"] === originalEntity["Id"]
                )
                {
                    parent[field] = null;
                }
            }
        }
    }

    Merge(formData: RepositoryDataCollection): void
    {
        const data = ObjectHelpers.getRepositoryFormData(formData);
        if (data.Entity && data.Type)
        {
            if (this.Entity)
            {
                Utilities.merge(this.Entity, data.Entity);
            }
            else
            {
                this.Add(data.Type, data.Entity, "");
            }
        }
        if (data.OtherEntities)
        {
            Utilities.mergeEntityCollections(this.OtherEntities, data.OtherEntities);
        }
    }

    HasChanged(): boolean
    {
        return this.HasEntityChanged(this.Entity) || Object.values(this.OtherEntities).some(entities => entities.some(entity => this.HasEntityChanged(entity)));
    }

    private HasEntityChanged(entity: any): boolean
    {
        if (!entity)
            return false;
        if (!Utilities.IsNullOrUndefined(entity["$modCount"]) && !Utilities.IsNullOrUndefined(entity["$initialModCount"]) && entity["$modCount"] > entity["$initialModCount"])
            return true;
        for (const field in entity)
        {
            if (!field.startsWith("$"))
            {
                let value = entity[field];
                if (Array.isArray(value) && value.some(x=>!!x && x["$isEntityWrapper"] && this.HasEntityChanged(x))) {
                    return true;
                }
                if (!!value && value["$isEntityWrapper"] && this.HasEntityChanged(value))
                    return true;
            }
        }
        return false;
    }

    SetInitialModCount(): void
    {
        this.SetInitialModCountEntity(this.Entity);
         Object.values(this.OtherEntities).forEach(entities => entities.forEach(entity => this.SetInitialModCountEntity(entity)));
    }

    SetInitialModCountEntity(entity: any, setChildren: boolean = true): void
    {
        if (!entity)
            return;
        entity["$initialModCount"] = entity["$modCount"];
        if (!setChildren)
            return;
        for (const field in entity)
        {
            if (!field.startsWith("$"))
            {
                let value = entity[field];
                if (Array.isArray(value))
                {
                    value.forEach(x=>
                    {
                        if(!!x && x["$isEntityWrapper"])
                            this.SetInitialModCountEntity(x);
                    });
                }
                if (!!value && value["$isEntityWrapper"])
                    this.SetInitialModCountEntity(value);
            }
        }
    }

    SetParentFormData(parentForm: RepositoryFormData, addMode: boolean): void
    {
        this.$ParentFormData = parentForm;
        if (!addMode)
            this.SyncOtherEntityCache(parentForm);
    }

    SyncOtherEntityCache(other: RepositoryFormData): void
    {
        const array = Object.keys(other.$OtherEntitiesByLoadFilter);
        for (let index = 0; index < array.length; index++)
        {
            const key= array[index];
            this.$OtherEntitiesByLoadFilter[key] = other.$OtherEntitiesByLoadFilter[key];
        }
    }

    static create(formData: RepositoryDataCollection): RepositoryFormData
    {
        if (!formData)
        {
            return null;
        }

        if (formData instanceof RepositoryFormData)
        {
            return formData;
        }

        const data = new RepositoryFormData(formData.Type, formData.Entity);
        data.OtherEntities = formData.OtherEntities;
        return data;
    }
}
